import React, { useContext, useMemo, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, Box, Collapse, Chip } from '@mui/material';
import { hexToPastel } from '../../../../../../utils/HexToPastelUtil';
import { CanvasContext } from '../../../WorkflowCanvas';
import useProjectStore from '../../../../../ProjectCanvas/useProjectStore';
import useCanvasStore from '../../../useCanvasStore';
import useZoomCollapse from '../../../../../../hooks/useZoomCollapse';
import { LoopHandles } from './ComponentNodeUtil/LoopHandles';
import AddNewComponentButton from './ComponentNodeUtil/AddNewComponentButton';
import AddConditionalPathButton from './ComponentNodeUtil/AddConditionalPathButton';
import './ComponentNode.css';

const BRANCH_PATH_TYPE = 'branch_paths';
const CONDITIONAL_WORKFLOW_PATH = 'conditional_workflow_path';

const NodeCard = styled('div')(({ theme }) => ({
  width: 520,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'visible',
  position: 'relative',
}));

const NodeHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'bgcolor' && prop !== 'collapsed',
})(({ theme, bgcolor, collapsed }) => ({
  position: 'relative',
  padding: collapsed ? '14px 18px' : '8px 12px',
  backgroundColor: bgcolor,
  borderRadius: collapsed ? 8 : '8px 8px 0 0',
  display: 'flex',
  flexDirection: 'column',
  gap: collapsed ? 4 : 2,
  transition: 'padding 0.2s ease, border-radius 0.2s ease, gap 0.2s ease',
}));

const portStyles = (theme, side) => ({
  '&&': {
    position: 'absolute',
    [side]: -7,
    top: 20,
    transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)',
    width: 14,
    height: 14,
    backgroundColor: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: '50%',
    zIndex: 10,
    cursor: 'crosshair',
    transition: 'transform 0.1s, background-color 0.1s',
    // Invisible hit area for easier grabbing
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -14,
      left: -14,
      right: -14,
      bottom: -14,
      borderRadius: '50%',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      width: 20,
      height: 20,
      transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)',
      transition: 'all 0.1s',
    },
  },
});

const PortLeft = styled(Handle)(({ theme }) => portStyles(theme, 'left'));
const PortRight = styled(Handle)(({ theme }) => portStyles(theme, 'right'));

/**
 * InlineNodeView replaces the old ComponentNodeView when inline editing is active.
 * Renders a clean card matching the NodeUI preview: colored header + config panel below.
 */
const InlineNodeView = ({
  data,
  nodeId,
  saving,
  isHovered,
  addLinkedConditionalPath,
  currentXPosition,
  currentYPosition,
  children,
  readOnly = false,
  selected = false,
}) => {
  const theme = useTheme();
  const zoomCollapsed = useZoomCollapse();
  const { pendingConnectionSource: pendingConnectionSourceContext, collapseNodes } = useContext(CanvasContext);
  const collapsed = (zoomCollapsed || collapseNodes) && !selected;
  const { hasInput, hasOutput, type, isTemp, baseColor, logo, label, displayName, description, schemaVersion } = data;
  const setPendingConnectionSource =
    Array.isArray(pendingConnectionSourceContext) && pendingConnectionSourceContext.length >= 2
      ? pendingConnectionSourceContext[1]
      : {};

  const isAiActive = useCanvasStore(s => s.aiActiveComponentIds.has(nodeId));

  const headerBg = useMemo(
    () => hexToPastel(baseColor || '#808080', theme.palette.mode),
    [baseColor, theme.palette.mode]
  );

  const handleAddComponentOnClick = (event) => {
    event.stopPropagation();
    if (setPendingConnectionSource) {
      setPendingConnectionSource({
        id: nodeId,
        position: { x: currentXPosition, y: currentYPosition },
        baseColor: data.baseColor,
        displayName: data.displayName,
      });
    }
    useProjectStore.getState().openComponentTray();
  };

  return (
    <NodeCard className={isAiActive ? 'ai-active-glow' : undefined}>
      {/* Left handle (input) */}
      {hasInput && (
        <PortLeft type="target" position={Position.Left} isConnectable={!isTemp} />
      )}

      {/* Right handle (output) */}
      {hasOutput && (
        <PortRight type="source" position={Position.Right} isConnectable={!isTemp} />
      )}

      {/* Branch path / conditional path buttons (hidden in readOnly) */}
      {!readOnly && hasOutput && type === BRANCH_PATH_TYPE && (
        <AddConditionalPathButton
          disabled={saving}
          onClick={addLinkedConditionalPath}
          classes={{
            addPathButton: 'inline-add-path-button',
            addPathIcon: 'inline-add-path-icon',
            addPathTooltipTitle: 'inline-add-path-tooltip',
          }}
          data={data}
        />
      )}
      {!readOnly && hasOutput && type !== BRANCH_PATH_TYPE && type !== CONDITIONAL_WORKFLOW_PATH && (
        <AddNewComponentButton
          disabled={saving}
          isHovered={isHovered}
          onClick={handleAddComponentOnClick}
          classes={{
            addPathButton: 'inline-add-path-button',
            addPathIcon: 'inline-add-path-icon',
            addPathTooltipTitle: 'inline-add-path-tooltip',
          }}
        />
      )}

      <LoopHandles />

      {/* Colored header: component name + type + description */}
      <NodeHeader bgcolor={headerBg} collapsed={collapsed}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: collapsed ? '10px' : '6px' }}>
          {logo && (
            <img
              src={logo}
              alt=""
              width={collapsed ? 32 : 20}
              height={collapsed ? 32 : 20}
              style={{ marginTop: 1, transition: 'width 0.2s ease, height 0.2s ease' }}
            />
          )}
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <Typography
              sx={{
                fontSize: collapsed ? 22 : 13,
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                transition: 'font-size 0.2s ease',
              }}
              title={label || displayName || type}
            >
              {label || displayName || type}
            </Typography>
            <Typography
              sx={{
                fontSize: collapsed ? 16 : 11,
                color: 'text.secondary',
                transition: 'font-size 0.2s ease',
              }}
            >
              {displayName || type}
            </Typography>
          </Box>
        </Box>
        {description && (
          <Typography
            sx={{
              fontSize: collapsed ? 15 : 11,
              color: 'text.disabled',
              ml: collapsed ? '42px' : '26px',
              transition: 'font-size 0.2s ease, margin-left 0.2s ease',
            }}
          >
            {description}
          </Typography>
        )}
        <Chip
          label={schemaVersion >= 2 ? 'v2' : 'v1'}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 6,
            height: 18,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.02em',
            backgroundColor: schemaVersion >= 2 ? 'rgba(33,150,243,0.15)' : 'rgba(0,0,0,0.08)',
            color: schemaVersion >= 2 ? '#1565c0' : 'text.secondary',
            border: 'none',
            '& .MuiChip-label': { px: '5px' },
          }}
        />
      </NodeHeader>

      {/* Config panel content — hidden when zoomed out (kept mounted to preserve state) */}
      <Collapse in={!collapsed} timeout={200} sx={{ position: 'relative' }}>
        {children}
        {isAiActive && <div className="ai-active-overlay" />}
      </Collapse>
    </NodeCard>
  );
};

export default React.memo(InlineNodeView);
